import React, {useState, useRef, useEffect} from "react";
import { Row, Col, Form } from "react-bootstrap";
import {$t, t_key} from "../../plugins/i18n";

type TwoFactorInputProps = {
    clearCodeInput: boolean;
    formId?: string;
    onCodeEnteredComplete: (code: string) => void;
    onCodeSubmitted: (code: string) => void;
    onCodeEdit?: () => void;
};

export default function TwoFactorInput({ onCodeEnteredComplete, onCodeSubmitted, onCodeEdit, clearCodeInput, formId = "tfa-form" }: TwoFactorInputProps) {
    const [digits, setDigits] = useState<string[]>(Array(6).fill(""));
    const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

    useEffect(() => {
        if (clearCodeInput) {
            setDigits(Array(6).fill(""));
            inputsRef.current[0]?.focus();
        }
    }, [clearCodeInput]);

    const handleChange = (value: string, idx: number) => {
        if (!/^\d*$/.test(value)) return;
        onCodeEdit?.();
        const nextDigit = value.slice(-1);
        const next = [...digits];
        next[idx] = nextDigit;
        setDigits(next);

        if (nextDigit && idx < 5) {
            inputsRef.current[idx + 1]?.focus();
        }

        if (next.every((d) => d !== "")) {
            onCodeEnteredComplete(next.join(""));
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
        if (e.key === "Backspace") {
            if (digits[idx]) {
                onCodeEdit?.();
                return;
            }

            if (!digits[idx] && idx > 0) {
                e.preventDefault();
                onCodeEdit?.();

                const next = [...digits];
                next[idx - 1] = "";
                setDigits(next);

                focusDigit(idx - 1);
            }
        }
        if (e.key === "Delete") {
            if (digits[idx]) {
                onCodeEdit?.();
                const next = [...digits];
                next[idx] = "";
                setDigits(next);
            }
            if (idx < 5) focusDigit(idx + 1);
        }
        if (e.key === "ArrowLeft" && idx > 0) {
            e.preventDefault();
            focusDigit(idx - 1);
        }
        if (e.key === "ArrowRight" && idx < 5) {
            e.preventDefault();
            focusDigit(idx + 1);
        }
        if (e.key === "Enter" && idx === 5) {
            (e.currentTarget.form as HTMLFormElement | null)?.requestSubmit();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
        const paste = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
        if (!paste) return;

        e.preventDefault();
        onCodeEdit?.();
        const filled = paste.split("").slice(0, 6);
        const next = [...filled, ...Array(6 - filled.length).fill("")];
        setDigits(next);

        const lastFilledIdx = Math.min(filled.length - 1, 5);

        if (paste.length === 6) {
            onCodeEnteredComplete(paste);
            focusDigit(lastFilledIdx);
        } else {
            if (lastFilledIdx >= 0) {
                focusDigit(lastFilledIdx);
            } else {
                const firstEmpty = next.findIndex((d) => d === "");
                if (firstEmpty !== -1) focusDigit(firstEmpty);
            }
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (digits.every((d) => d !== "")) {
            onCodeEnteredComplete(digits.join(""));
            onCodeSubmitted(digits.join(""));
        }
    };

    const focusDigit = (i: number) => {
        requestAnimationFrame(() => {
            const el = inputsRef.current[i];
            if (el) {
                el.focus();
                const len = el.value.length;
                try {
                    el.setSelectionRange(len, len);
                } catch {
                }
            }
        });
    };

    return (
        <Form id={formId} onSubmit={handleSubmit}>
        <Row className="g-3 justify-content-center" onPaste={handlePaste}>
            <Col className="otp-input-row">
                {digits.map((digit, idx) => {
                    const id = `otp-${idx}`;
                    const fieldLabel = $t(t_key.login_view.tfa_otp_field, { n: idx + 1, m: digits.length });

                    return (
                        <div key={id}>
                            <Form.Control
                                id={id}
                                key={idx}
                                type="text"
                                inputMode="numeric"
                                className={`otp-box ${clearCodeInput ? "is-invalid" : ""}`}
                                value={digit}
                                ref={(el: HTMLInputElement | null) => {
                                    inputsRef.current[idx] = el;
                                }}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    handleChange(e.target.value, idx)
                                }
                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                                    handleKeyDown(e, idx)
                                }
                                autoComplete={idx === 0 ? "one-time-code" : "off"}
                                aria-label={fieldLabel}
                                aria-invalid={clearCodeInput || undefined}
                            />
                        </div>
                    )
                })}
            </Col>
        </Row>
        </Form>
    );
}
